import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useMemo } from "react";
import { useAppShellStore } from "../../app-shell";
import { TextInput } from "../../form";
import { useSearchForm } from "../../hooks";
import { cn } from "../../utils";
import type { NavigationItemType, NavigationProps } from "../types";
import { SidebarNavigationItem } from "./sidebar-navigation-item";

/** Props Interface */
interface SidebarNavigationMenuProps extends Pick<NavigationProps, "navigationItems"> {}

export function SidebarNavigationItems({ navigationItems }: SidebarNavigationMenuProps) {
	/** Hooks */
	const { sidebarIsCollapsed } = useAppShellStore();
	const searchForm = useSearchForm("sidebar-navigation", 150, true);

	/** Computed */
	const searchValue = sidebarIsCollapsed ? "" : (searchForm.searchValue?.trim() ?? "");
	const isSearching = searchValue.length > 0;
	const [parent] = useAutoAnimate();

	/**
	 * While searching, the grouped structure is flattened to its leaf items so that
	 * a match on a single child no longer pulls in its entire group. Without a search
	 * value, the original grouped items are rendered untouched.
	 */
	const visibleItems = useMemo(() => {
		if (!isSearching) return navigationItems;

		const lowerSearch = searchValue.toLowerCase();
		const matches: Array<NavigationItemType> = [];

		function collect(items: Array<NavigationItemType | string>) {
			for (const item of items) {
				if (typeof item === "string") continue;
				if (item.items?.length) {
					collect(item.items);
				} else if (item.to && item.label.toLowerCase().includes(lowerSearch)) {
					matches.push(item);
				}
			}
		}

		collect(navigationItems);
		return matches;
	}, [navigationItems, isSearching, searchValue]);

	/** Render */
	return (
		<div
			className={cn(
				"h-full min-h-0 overflow-y-auto overflow-x-hidden py-4 flex flex-col gap-0 pb-[100px] relative",
				sidebarIsCollapsed && "scrollbar-hidden",
			)}
			ref={parent}
		>
			{!sidebarIsCollapsed && (
				<div className="pl-2 pr-2 mb-2">
					<TextInput {...searchForm.getSearchInputProps()} />
				</div>
			)}
			{visibleItems.map((navigationItem) => (
				<SidebarNavigationItem
					key={navigationItem.label}
					navigationItem={navigationItem}
				/>
			))}
		</div>
	);
}
